import { useQuery } from "@powersync/react";
import { PMIcon } from "@/ds/vendor";
import type { Observation, Visit } from "@/data/queries";
import { useApp } from "@/app/AppContext";
import { levelDef } from "@/lib/levels";
import { MediaThumb, VisitStatusBadge } from "./visit";
import { clock, shortDate, duration } from "./format";
import { visitStatus } from "./visitStatus";

// preview/components-fieldvisit-card.html, taken further: status pill · title ·
// AI summary · PHOTOS gallery (+N) · ARTIFACTS chips · footer time + View details.
// Adds the location's level emoji, voice-memo count, and a processing shimmer.
export function FieldVisitCard({ v, onOpen, compact = false }: { v: Visit; onOpen: () => void; compact?: boolean }) {
  const { hierarchy } = useApp();
  const d = levelDef(v.location_level, hierarchy.key);
  const photos = useQuery<Observation>("SELECT * FROM mobile_field_note_media WHERE field_note_id = ? AND content_type LIKE 'image/%' ORDER BY created_at LIMIT 4", [v.id]).data;
  const s = visitStatus(v);
  const nPhotos = v.photo_count ?? 0;
  const shown = compact ? 3 : 4;
  const artifacts = [
    v.report_id && { kind: "pdf", label: "Visit report", sub: v.report_approved_at ? "Signed" : "Draft" },
    (v.audio_count ?? 0) > 0 && { kind: "aud", label: "Voice memos", sub: `${v.audio_count} recorded` },
    s === "processing" && { kind: "ai", label: "AI summary", sub: "Writing…" },
  ].filter(Boolean) as { kind: string; label: string; sub: string }[];

  return (
    <article className={`fvc pm-lift ${compact ? "fvc--compact" : ""} ${s === "processing" ? "fvc--proc" : ""}`} onClick={onOpen} tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onOpen()} aria-label={`${v.location_label} visit`}>
      <header className="fvc-top">
        <VisitStatusBadge v={v} />
        <span className="fvc-where">{d.emoji} {v.location_label}</span>
      </header>
      <h3 className="fvc-title">{v.location_label || v.title} — {shortDate(v.started_at)} visit</h3>
      <p className={`fvc-summary ${compact ? "clamp-2" : "clamp-3"}`}>
        {v.ai_summary || (s === "in_progress" ? "Visit in progress. Observations stream in as the phone syncs." : s === "processing" ? "Transcribing voice memos and writing the summary." : "No summary for this visit.")}
      </p>

      {nPhotos > 0 && (
        <>
          <div className="fvc-label"><PMIcon name="camera-outline" size={15} /> Photos · {nPhotos}</div>
          <div className="fvc-gallery">
            {photos.slice(0, shown).map((p, i) => (
              <div className="fvc-thumb" key={p.id}>
                <MediaThumb o={p} alt={i} />
                {i === shown - 1 && nPhotos > shown && <span className="fvc-more">+{nPhotos - shown + 1}</span>}
              </div>
            ))}
          </div>
        </>
      )}

      {!compact && artifacts.length > 0 && (
        <>
          <div className="fvc-label"><PMIcon name="link-outline" size={15} /> Artifacts · {artifacts.length}</div>
          <div className="fvc-artifacts">
            {artifacts.map((a) => (
              <span className={`fvc-art fvc-art--${a.kind}`} key={a.label}>
                <i>{a.kind === "pdf" ? "PDF" : a.kind === "aud" ? "🎙️" : "AI"}</i>
                <span><strong>{a.label}</strong><small>{a.sub}</small></span>
              </span>
            ))}
          </div>
        </>
      )}

      <footer className="fvc-foot">
        <span className="fvc-time"><PMIcon name="clock-outline" size={15} /> {shortDate(v.started_at)}, {clock(v.started_at)}{duration(v.started_at, v.completed_at) ? ` · ${duration(v.started_at, v.completed_at)}` : ""}</span>
        <span className="fvc-go">View details <PMIcon name="arrow-right" size={16} /></span>
      </footer>
    </article>
  );
}

// preview/components-info-toolbar.html: Started / Completed pine pills.
export function TimePills({ started, completed, extra }: { started?: string | null; completed?: string | null; extra?: React.ReactNode }) {
  return (
    <div className="timepills">
      <div className="timepill"><span><PMIcon name="clock-outline" size={16} /> Started</span><strong>{clock(started)}</strong></div>
      <div className={`timepill ${completed ? "" : "timepill--open"}`}><span><PMIcon name="check-circle-outline" size={16} /> {completed ? "Completed" : "In progress"}</span><strong>{completed ? clock(completed) : "…"}</strong></div>
      {extra}
    </div>
  );
}
